// src/modules/app-views/_apps/appsindico/units/pages/Document/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import useZustand from 'hooks/useZustand';
import { Tabs } from 'antd';
import translateX from 'utils/translateX';
import { useEffect, useMemo } from 'react';
import { useDocumentContext } from 'components/core/DocumentProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { buildInitialValues } from 'utils/mock';
import useFormInstance from 'hooks/useFormInstance';
import CardHeader from 'layouts/Pages/PageHeader/CardHeader';
import RecordTab from './tabs/RecordTab';
import TitularTab from './tabs/TitularTab';
import HabitationTab from './tabs/HabitationTab';
import DependentsTab from './tabs/DependentsTab';
import VehiclesTab from './tabs/VehiclesTab';
import BicyclesTab from './tabs/BicyclesTab';
import PetsTab from './tabs/PetsTab';
import ParkingSpotsTab from './tabs/ParkingSpotsTab';
import PhotosTab from './tabs/PhotosTab';
import AgreementsTab from './tabs/AgreementsTab';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const isMockStep = false;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const UnitsDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode } =
    useDocumentContext();

  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { upsert, urlInfo } = useZustand('global', 'useApi');
  const { formRef, isInitializedRef } = useFormInstance({ data });
  const _id = urlInfo?.params?._id;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const tabsItems = useMemo(() => {
    const src = Array.isArray(availableTabs)
      ? availableTabs
      : availableTabs
        ? Object.values(availableTabs)
        : [];
    return src;
  }, [availableTabs]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const initialValues = useMemo(() => buildInitialValues(isMockStep, data), [data]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const childProps = { ...props, data, initTab, viewMode };
    const itemsLocal = buildTabsArray(childProps);
    buildTabs(itemsLocal);
  }, [initTab, viewMode, storedLanguage]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ values, allValues }) => {
    const documentId = values?._id || allValues?._id || data?._id;
    const body = { ...values };
    await upsert({ dataSet: body, _id: documentId });
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          showSubmitButton={false}
          initFocus="room"
          reloadEntirePage={true}
          ignoreResetFormOnSubmit={true}
          locked={viewMode === 'view'}
          showInfoMessage={false}
          initialValues={initialValues}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div style={{ position: 'relative' }}>
            <CardHeader
              overlapsBackground
              data={data}
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default UnitsDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function buildTabsArray(props) {
  const { initTab, viewMode } = props;
  const isNewDocument = viewMode === 'add';

  return [
    {
      key: initTab,
      label: <span>{translateX('record')}</span>,
      hide: false,
      disabled: false,
      children: <RecordTab />,
    },
    {
      key: 'titular',
      label: <span>{translateX('partner_owner')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <TitularTab />,
    },
    {
      key: 'habitation',
      label: <span>{translateX('habitation')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <HabitationTab />,
    },
    {
      key: 'dependents',
      label: <span>{translateX('dependents')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <DependentsTab />,
    },
    {
      key: 'vehicles',
      label: <span>{translateX('vehicles')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <VehiclesTab />,
    },
    {
      key: 'bicycles',
      label: <span>{translateX('bicycles')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <BicyclesTab />,
    },
    {
      key: 'pets',
      label: <span>{translateX('pets')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <PetsTab />,
    },
    {
      key: 'parkingSpots',
      label: <span>{translateX('parking_spots')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <ParkingSpotsTab />,
    },
    {
      key: 'photos',
      label: <span>{translateX('photos')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <PhotosTab />,
    },
    {
      key: 'agreements',
      label: <span>{translateX('agreements')}</span>,
      hide: false,
      disabled: isNewDocument,
      children: <AgreementsTab />,
    },
  ];
}
